import { Category } from '@firmable/contracts';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

/**
 * Raw notification data from Novu
 *
 * * Since Novu sending data in string format, We already
 * raised to Novu team to fix this issue.
 */
type RawNotificationData = Record<string, unknown>;

/**
 * Fields common to every signal-agent notification.
 */
type CommonNotificationData = {
  agentId: number;
  signalType: {
    slug: string;
    category: Category;
  };
  signalCount: number;
};

/**
 * Legacy notifications scope signals by list + incremental run; they never carry an `agentRunId`.
 */
type LegacyNotificationData = CommonNotificationData & {
  listIds: string[];
  incrementalRunIds: string[];
  agentRunId?: never;
};

/**
 * Newer notifications scope signals by a single `agentRunId` and don't carry the legacy fields.
 */
type RunNotificationData = CommonNotificationData & {
  agentRunId: string;
  listIds?: never;
  incrementalRunIds?: never;
};

/**
 * Transformed notification data — the two shapes are mutually exclusive.
 */
type TransformedOpenedNotificationData = LegacyNotificationData | RunNotificationData;

export interface NotificationState {
  openedNotificationData: TransformedOpenedNotificationData | null;
}

export interface NotificationAction {
  setOpenedNotificationData: (rawNotificationData: RawNotificationData | null) => void;
  clearOpenedNotificationData: () => void;
}
/**
 * Store to maintain particular opened (when user clicks on notification) notification data
 */
const useOpenedSignalNotification = create<NotificationState & NotificationAction>()(
  devtools(
    immer((set) => ({
      openedNotificationData: null,
      setOpenedNotificationData: (rawNotificationData: RawNotificationData | null) =>
        set(
          (state) => {
            // Parse the common fields first — they appear on every variant.
            const common: CommonNotificationData = {
              agentId: 0,
              signalType: { slug: '', category: 'company' },
              signalCount: 0,
            };

            if (rawNotificationData?.signalCount) {
              common.signalCount = Number(rawNotificationData.signalCount);
            }
            if (rawNotificationData?.agentId) {
              common.agentId = Number(rawNotificationData.agentId);
            }
            if (rawNotificationData?.signalType) {
              // to convert string to object, as novu sends the signal type as a string
              const transformedSignalType = new Function('return' + (rawNotificationData.signalType ?? '{}'))();
              common.signalType = {
                slug: transformedSignalType.slug,
                category: transformedSignalType.category,
              };
            }

            // Prefer the run-scoped variant when `agentRunId` is present; legacy fields are ignored.
            if (rawNotificationData?.agentRunId) {
              state.openedNotificationData = {
                ...common,
                agentRunId: String(rawNotificationData.agentRunId),
              };
              return;
            }

            // Legacy fallback — list + incremental-run scope.
            let listIds: string[] = [];
            let incrementalRunIds: string[] = [];
            if (rawNotificationData?.listIds) {
              // to convert string to array, as novu sends the list ids as a string
              listIds = eval((rawNotificationData.listIds as string) ?? '[]');
            }
            if (rawNotificationData?.incrementalRunIds) {
              // to convert string to array, as novu sends the incremental run ids as a string
              incrementalRunIds = eval((rawNotificationData.incrementalRunIds as string) ?? '[]');
            }

            state.openedNotificationData = {
              ...common,
              listIds,
              incrementalRunIds,
            };
          },
          false,
          { type: 'SET_OPENED_NOTIFICATION_DATA' },
        ),
      clearOpenedNotificationData: () =>
        set(
          (state) => {
            state.openedNotificationData = null;
          },
          false,
          { type: 'CLEAR_OPENED_NOTIFICATION_DATA' },
        ),
    })),
    {
      store: 'OpenedSignalNotification',
    },
  ),
);

export { useOpenedSignalNotification };
